import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import type { RecurrenceRule, RecurrenceScope, Task } from '../../types/task';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Alert } from '../atoms/Alert/Alert';
import { FieldError } from '../atoms/FieldError/FieldError';
import { Text } from '../atoms/Typography/Text';
import { Dialog } from '../ui/Dialog/Dialog';
import { ruleEndDate, useTaskFormat } from './taskFormat';
import { EMAIL_PREFERENCES_QUERY_KEY, fetchEmailPreferences } from '../../services/emailPreferencesService';
import './TaskForm.css';

const EMAIL_SETTINGS_PATH = '/settings#email-notifications';

/** Link to Settings → E-Mail-Benachrichtigungen (a plain link outside a router, e.g. in isolated tests). */
function SettingsLink({ className, children }: { className?: string; children: ReactNode }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link to={EMAIL_SETTINGS_PATH} className={className}>{children}</Link>
  ) : (
    <a href={EMAIL_SETTINGS_PATH} className={className}>{children}</a>
  );
}

const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
const CATEGORIES = [
  'maintenance',
  'feeding',
  'water_testing',
  'dosing',
  'equipment',
  'livestock',
  'cleaning',
  'observation',
  'custom',
] as const;
const STATUSES = ['open', 'in_progress', 'completed', 'skipped', 'archived'] as const;
const FREQUENCY_TYPES = ['daily', 'every_x_days', 'weekly', 'specific_weekdays', 'monthly', 'custom'] as const;

type Frequency = typeof FREQUENCY_TYPES[number];

function buildTaskFormSchema(t: TFunction) {
  return z.object({
    title: z.string().trim().min(1, t('tasks.form.titleRequired')).max(200, t('tasks.form.titleTooLong')),
    description: z.string().max(2000, t('tasks.form.descriptionTooLong')).optional(),
    priority: z.enum(PRIORITIES),
    category: z.enum(CATEGORIES),
    status: z.enum(STATUSES),
    dueDate: z.string().optional(),
    dueTime: z.string().optional(),
    frequency: z.enum(FREQUENCY_TYPES).optional(),
    /** Kept as text in the form; parsed on submit. */
    recurrenceIntervalDays: z.string().optional(),
    recurrenceWeekdays: z.array(z.number().int().min(0).max(6)).optional(),
    recurrenceEndDate: z.string().optional(),
    /** "Am Fälligkeitstag per E-Mail erinnern" — series-level for a recurring task. */
    emailReminder: z.boolean().optional(),
  });
}

type TaskFormFields = z.infer<ReturnType<typeof buildTaskFormSchema>>;

/** What the form hands to its caller. */
export type TaskFormValues = Omit<TaskFormFields, 'recurrenceIntervalDays'> & {
  recurrenceIntervalDays?: number;
};

export type TaskFormSubmitValues = TaskFormValues & {
  aquariumId: string;
  id?: string;
  recurrenceRule?: RecurrenceRule | null;
  /** Recurring task: which occurrences the edit applies to. */
  scope?: RecurrenceScope;
};

interface TaskFormProps {
  aquariumId: string;
  task?: Task | null;
  /** Editing a recurring task: what the user chose to change. */
  scope?: RecurrenceScope;
  onSubmit: (values: TaskFormSubmitValues) => Promise<void>;
  onClose: () => void;
  isSubmitting?: boolean;
  /** New task: due date to prefill (YYYY-MM-DD), e.g. the day tapped in the calendar. */
  initialDueDate?: string;
}

function defaultFields(task: Task | null | undefined, initialDueDate?: string): TaskFormFields {
  if (!task) {
    return {
      title: '',
      description: '',
      priority: 'medium',
      category: 'maintenance',
      status: 'open',
      dueDate: initialDueDate ?? '',
      dueTime: '',
      frequency: 'weekly',
      recurrenceIntervalDays: '',
      recurrenceWeekdays: [],
      recurrenceEndDate: '',
      emailReminder: false,
    };
  }
  const rule = task.recurrenceRule;
  return {
    title: task.title,
    description: task.description ?? '',
    priority: task.priority,
    category: (CATEGORIES as readonly string[]).includes(task.category) ? task.category : 'custom',
    status: task.status,
    dueDate: task.dueDate?.slice(0, 10) ?? '',
    dueTime: task.dueTime?.slice(0, 5) ?? '',
    frequency: (rule?.frequency as Frequency | undefined) ?? 'weekly',
    recurrenceIntervalDays: rule?.interval != null ? String(rule.interval) : '',
    recurrenceWeekdays: rule?.weekdays ?? [],
    recurrenceEndDate: ruleEndDate(rule) ?? '',
    emailReminder: task.emailReminder ?? false,
  };
}

export function TaskForm({
  aquariumId,
  task,
  scope,
  onSubmit,
  onClose,
  isSubmitting = false,
  initialDueDate,
}: TaskFormProps) {
  const { t } = useTranslation();
  const fmt = useTaskFormat();
  const isEdit = Boolean(task);
  const recurringTask = Boolean(task?.recurrenceRule);
  /** Editing one occurrence never changes the rule; status is per occurrence. */
  const editsOccurrenceOnly = isEdit && recurringTask && scope === 'occurrence';
  const showRecurrence = !editsOccurrenceOnly;
  const showStatus = isEdit && (!recurringTask || scope === 'occurrence');

  const [recurrenceEnabled, setRecurrenceEnabled] = useState(recurringTask);
  const [formError, setFormError] = useState(false);
  const [weekdaysError, setWeekdaysError] = useState(false);

  const schema = useMemo(() => buildTaskFormSchema(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<TaskFormFields>({
    resolver: zodResolver(schema),
    defaultValues: defaultFields(task, initialDueDate),
  });

  useEffect(() => {
    if (task) {
      setRecurrenceEnabled(Boolean(task.recurrenceRule));
      reset(defaultFields(task));
    }
  }, [task, reset]);

  const frequency = watch('frequency');
  const emailReminder = watch('emailReminder') ?? false;
  const emailPrefs = useQuery({
    queryKey: EMAIL_PREFERENCES_QUERY_KEY,
    queryFn: fetchEmailPreferences,
    retry: false,
    staleTime: 60_000,
    // Only needed for the hint next to a ticked checkbox.
    enabled: emailReminder,
  });
  // Shown only when we know for sure that task reminders are switched off.
  const remindersOff = emailPrefs.data !== undefined && !emailPrefs.data.taskReminders;
  const recurrenceWeekdays = watch('recurrenceWeekdays') ?? [];
  const usesInterval = frequency === 'every_x_days' || frequency === 'custom';

  function toggleWeekday(day: number) {
    const next = recurrenceWeekdays.includes(day)
      ? recurrenceWeekdays.filter((d) => d !== day)
      : [...recurrenceWeekdays, day].sort();
    setValue('recurrenceWeekdays', next);
    if (next.length > 0) setWeekdaysError(false);
  }

  async function handleFormSubmit(fields: TaskFormFields) {
    setFormError(false);
    let recurrenceRule: RecurrenceRule | null = null;
    let intervalDays: number | undefined;

    if (showRecurrence && recurrenceEnabled && fields.frequency) {
      if (usesInterval) {
        intervalDays = Number(fields.recurrenceIntervalDays);
        if (!Number.isInteger(intervalDays) || intervalDays < 1 || intervalDays > 365) {
          setError('recurrenceIntervalDays', { message: t('tasks.form.intervalInvalid') });
          return;
        }
      }
      if (fields.frequency === 'specific_weekdays' && (fields.recurrenceWeekdays ?? []).length === 0) {
        setWeekdaysError(true);
        return;
      }
      recurrenceRule = {
        frequency: fields.frequency,
        ...(usesInterval ? { interval: intervalDays } : {}),
        ...(fields.frequency === 'specific_weekdays' ? { weekdays: fields.recurrenceWeekdays } : {}),
        endDate: fields.recurrenceEndDate || null,
      };
    }

    const payload: TaskFormSubmitValues = {
      ...fields,
      recurrenceIntervalDays: intervalDays,
      aquariumId,
      ...(task?.id ? { id: task.id } : {}),
      ...(scope ? { scope } : {}),
      // Editing one occurrence leaves the series rule alone.
      ...(showRecurrence ? { recurrenceRule } : {}),
    };

    try {
      await onSubmit(payload);
      onClose();
    } catch {
      setFormError(true);
    }
  }

  const title = isEdit ? t('tasks.form.editTitle') : t('tasks.form.createTitle');
  const occurrenceDate = task?.occurrenceDate ?? task?.dueDate ?? null;

  return (
    <Dialog isOpen onClose={onClose} title={title} data-testid="task-form">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form className="task-form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        {isEdit && recurringTask && scope && (
          <Text size="sm" color="muted" data-testid="task-form-scope-note">
            {t(`tasks.form.scopeNote.${scope}`, { date: occurrenceDate ? fmt.date(occurrenceDate) : '' })}
          </Text>
        )}

        <Input
          id="task-title"
          label={t('tasks.form.titleLabel')}
          required
          type="text"
          placeholder={t('tasks.form.titlePlaceholder')}
          error={errors.title?.message}
          fullWidth
          {...register('title')}
        />

        <Textarea
          id="task-description"
          label={t('tasks.form.descriptionLabel')}
          placeholder={t('tasks.form.descriptionPlaceholder')}
          rows={3}
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="task-form__row">
          <Select id="task-priority" label={t('tasks.form.priorityLabel')} {...register('priority')}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{t(`tasks.form.priorities.${p}`)}</option>
            ))}
          </Select>

          <Select id="task-category" label={t('tasks.form.categoryLabel')} {...register('category')}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{t(`tasks.form.categories.${c}`)}</option>
            ))}
          </Select>
        </div>

        {showStatus && (
          <Select id="task-status" label={t('tasks.form.statusLabel')} {...register('status')}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{t(`tasks.form.statuses.${s}`)}</option>
            ))}
          </Select>
        )}

        <div className="task-form__row">
          <Input id="task-due-date" label={t('tasks.form.dueDateLabel')} type="date" fullWidth {...register('dueDate')} />
          <Input id="task-due-time" label={t('tasks.form.dueTimeLabel')} type="time" fullWidth {...register('dueTime')} />
        </div>

        <div className="task-form__email-reminder">
          <Checkbox
            id="task-email-reminder"
            label={t('tasks.form.emailReminderLabel')}
            aria-describedby={[
              recurringTask || recurrenceEnabled ? 'task-email-reminder-series' : '',
              emailReminder && remindersOff ? 'task-email-reminder-off' : '',
            ].filter(Boolean).join(' ') || undefined}
            {...register('emailReminder')}
          />
          {(recurringTask || recurrenceEnabled) && (
            <Text id="task-email-reminder-series" size="sm" color="muted">
              {t('tasks.form.emailReminderSeries')}
            </Text>
          )}
          {emailReminder && remindersOff && (
            <Text id="task-email-reminder-off" size="sm" data-testid="task-email-reminder-off">
              {t('tasks.form.emailReminderOff')}{' '}
              <SettingsLink className="task-form__link">{t('tasks.form.emailReminderSettingsLink')}</SettingsLink>
            </Text>
          )}
        </div>

        {showRecurrence && (
          <Checkbox
            id="task-recurrence-toggle"
            label={t('tasks.form.recurringLabel')}
            checked={recurrenceEnabled}
            onChange={(e) => setRecurrenceEnabled(e.target.checked)}
            aria-controls="task-recurrence-options"
          />
        )}

        {showRecurrence && recurrenceEnabled && (
          <div id="task-recurrence-options" className="task-form__recurrence">
            <Select id="task-frequency" label={t('tasks.form.frequencyLabel')} {...register('frequency')}>
              <option value="daily">{t('tasks.form.frequencyDaily')}</option>
              <option value="every_x_days">{t('tasks.form.frequencyEveryXDays')}</option>
              <option value="weekly">{t('tasks.form.frequencyWeekly')}</option>
              <option value="specific_weekdays">{t('tasks.form.frequencySpecificWeekdays')}</option>
              <option value="monthly">{t('tasks.form.frequencyMonthly')}</option>
              <option value="custom">{t('tasks.form.frequencyCustom')}</option>
            </Select>

            {usesInterval && (
              <Input
                id="task-recurrence-interval"
                label={t('tasks.form.intervalLabel')}
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                placeholder={t('tasks.form.intervalPlaceholder')}
                error={errors.recurrenceIntervalDays?.message}
                {...register('recurrenceIntervalDays')}
              />
            )}

            {frequency === 'specific_weekdays' && (
              <div className="task-form__weekdays-field">
                <span id="task-weekdays-label" className="task-form__label">{t('tasks.form.repeatOnLabel')}</span>
                <div
                  className="task-form__weekdays"
                  role="group"
                  aria-labelledby="task-weekdays-label"
                  aria-describedby={weekdaysError ? 'task-weekdays-error' : undefined}
                >
                  {fmt.weekdays('short').map(({ day, label }) => (
                    <Button
                      key={day}
                      type="button"
                      size="sm"
                      variant={recurrenceWeekdays.includes(day) ? 'primary' : 'secondary'}
                      aria-pressed={recurrenceWeekdays.includes(day)}
                      aria-label={fmt.weekdayName(day, 'long')}
                      onClick={() => toggleWeekday(day)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                {weekdaysError && <FieldError id="task-weekdays-error">{t('tasks.form.weekdaysRequired')}</FieldError>}
              </div>
            )}

            <Input
              id="task-recurrence-end"
              label={t('tasks.form.recurrenceEndLabel')}
              type="date"
              helperText={t('tasks.form.recurrenceEndHelp')}
              fullWidth
              {...register('recurrenceEndDate')}
            />
          </div>
        )}

        {formError && <Alert compact>{t('tasks.form.submitError')}</Alert>}

        <div className="task-form__actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('tasks.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
            {isEdit ? t('tasks.form.save') : t('tasks.form.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
